import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  isUUID,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { requireListingImage } from '../../util/configHelpers';
import { getBookingDurationUnit } from '../../util/bookingDuration';
import { isFixdeskListing } from '../../util/fixdesk';
import { isVerifiedProvider as isVerifiedProviderUser } from '../../util/verifiedProvider';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isIndexableListing,
  listingPhotoAltTitle,
  listingSeoDescription,
  listingSeoTitle,
} from '../../util/listingSeo';
import { listingSchemaGraph } from '../../util/listingStructuredData';
import {
  OFFER,
  REQUEST,
  isBookingProcess,
  isNegotiationProcess,
  isPurchaseProcess,
  isAboProcessAlias,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities, getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  SectionText,
  VerifiedProviderBanner,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  clearInquiryIntent,
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleNavigateToMakeOfferPage,
  handleNavigateToRequestQuotePage,
  handleSubmit,
  usePendingBookingResume,
  useTrackListingView,
} from './ListingPage.shared';
// v11.0.0 moved this into the Notifications sub-folder; props are unchanged, so the local
// alias stays and the JSX below is untouched.
import ActionBarMaybe from './Notifications/ActionBar';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionLocationFullBleed from './SectionLocationFullBleed';
import SectionPhotoMosaic, { GalleryModal } from './SectionPhotoMosaic';
import SectionTitleBar from './SectionTitleBar';
import SectionAwardsMaybe from './SectionAwardsMaybe';
import SectionHighlights from './SectionHighlights';
import SectionDescriptionExpandable from './SectionDescriptionExpandable';
import SectionSpacesMaybe from './SectionSpacesMaybe';
import SectionPracticalities from './SectionPracticalities';
import SectionThingsToKnow from './SectionThingsToKnow';
import CustomListingFields from './CustomListingFields';
import SectionOtherListingsMaybe from './SectionOtherListingsMaybe';
import SectionCityLinkMaybe from './SectionCityLinkMaybe';

import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [mounted, setMounted] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  useEffect(() => {
    setMounted(true);
  }, []);

  // A logged-out visitor who presses "contact host" gets the login dialog on top of this page, and
  // handleContactUser leaves the listing id behind as the record of what they were trying to do.
  // The dialog resolves without leaving the page, so this component never remounts and the
  // useState initialiser above cannot fire a second time — the intent has to be picked up here
  // instead, the moment the login lands. Depending on the authenticated flag rather than the id
  // alone is what stops the inquiry from reopening after the visitor closes it.
  const { inquiryModalOpenForListingId } = props;
  const listingIdParam = props.params.id;
  useEffect(() => {
    if (props.isAuthenticated && inquiryModalOpenForListingId === listingIdParam) {
      setInquiryModalOpen(true);
    }
  }, [props.isAuthenticated, inquiryModalOpenForListingId, listingIdParam]);

  const {
    isAuthenticated,
    currentUser,
    stripeStatusSynced,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    authorListings = [],
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    onClearInquiryIntent,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    ...restOfProps
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  // Muss vor jedem frühen return stehen — Hooks dürfen nicht bedingt laufen.
  useTrackListingView(
    currentListing,
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
  );

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  // Replay a booking that was put together before the visitor had an account. Must sit above the
  // loading / error early-returns below: it is a hook.
  usePendingBookingResume({
    isAuthenticated,
    currentUser,
    listing: currentListing,
    params,
    history,
    location,
    routes: routeConfiguration,
    getListing,
    callSetInitialValues,
    onInitializeCardPaymentData,
  });

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (!isUUID(rawParams.id) || (showListingError && showListingError.status === 404)) {
    // 404 listing not found — or an id that can never be one (/l/foo), which loadData does not
    // even ask the API about.
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing: temporary, the server answers 503 (see ErrorPage).
    return (
      <ErrorPage
        topbar={topbar}
        scrollingDisabled={scrollingDisabled}
        intl={intl}
        staticContext={props.staticContext}
      />
    );
  } else if (!currentListing.id) {
    // Still loading the listing (in SSR: the data never arrived → 503, see LoadingPage)
    return (
      <LoadingPage
        topbar={topbar}
        scrollingDisabled={scrollingDisabled}
        intl={intl}
        staticContext={props.staticContext}
      />
    );
  }

  const {
    description = '',
    geolocation = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage
        topbar={topbar}
        scrollingDisabled={scrollingDisabled}
        intl={intl}
        staticContext={props.staticContext}
        invalidListing
      />
    );
  }
  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);
  const showDescription = foundListingTypeConfig?.defaultListingFields?.description;

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isAbo = isAboProcessAlias(transactionProcessAlias);
  // TEMPORARY fixdesk fallback (see util/fixdesk.js): the purchase price buys one
  // month, so the hero chip reads "Monatlich buchbar" like the ListingCard chip.
  const isFixdesk = isFixdeskListing(publicData);
  const bookingDurationUnit = getBookingDurationUnit(publicData) || (isFixdesk ? 'month' : null);
  const isPurchase = isPurchaseProcess(processName);
  const isNegotiation = isNegotiationProcess(processName);
  const processType = isBooking
    ? 'booking'
    : isPurchase
    ? 'purchase'
    : isNegotiation
    ? 'negotiation'
    : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const authorNeedsPayoutDetails =
    ['booking', 'purchase'].includes(processType) || (isNegotiation && unitType === OFFER);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && authorNeedsPayoutDetails && !currentUser?.attributes?.stripeConnected;
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsMissingNotice" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    isAuthenticated,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquire transition to inquiry state in booking, purchase and negotiation processes.
  // Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
  const onNavigateToMakeOfferPage = handleNavigateToMakeOfferPage({
    ...commonParams,
    getListing,
  });
  // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
  const onNavigateToRequestQuotePage = handleNavigateToRequestQuotePage({
    ...commonParams,
    getListing,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    isAuthenticated,
    location,
    callSetInitialValues,
    onClearInquiryIntent,
    getListing,
    onInitializeCardPaymentData,
    onSendInquiry,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else if (isNegotiation && unitType === REQUEST) {
      onNavigateToMakeOfferPage(values);
    } else if (isNegotiation && unitType === OFFER) {
      onNavigateToRequestQuotePage(values);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  // <title>/og:title and the meta description are built in util/listingSeo.js with NEW keys:
  // ListingPage.schemaTitle is Console-hosted, a repo edit of it would be a silent no-op. The H1
  // stays the provider's own title. Product + BreadcrumbList JSON-LD: util/listingStructuredData.js
  // (canonical /l/<uuid> everywhere, LoungePair lounges as AggregateOffer, ratings only from real
  // reviews).
  const seoTitle = listingSeoTitle({
    listing: currentListing,
    intl,
    marketplaceName: config.marketplaceName,
  });
  const seoDescription = listingSeoDescription({ listing: currentListing, intl });
  const photoAltTitle = listingPhotoAltTitle(currentListing, intl);
  const schemaGraph = listingSchemaGraph({ listing: currentListing, config, intl, reviews });

  // Derive aggregate rating from reviews (if any have numeric rating)
  const ratingValues = (reviews || [])
    .map(r => r?.attributes?.rating)
    .filter(v => typeof v === 'number' && v > 0);
  const avgRating =
    ratingValues.length > 0
      ? ratingValues.reduce((a, b) => a + b, 0) / ratingValues.length
      : undefined;

  // Derive highlight rows from publicData
  const derivedHighlights = [];
  if (publicData?.access_24_7 || publicData?.accessType === '24/7') {
    derivedHighlights.push({
      iconKey: 'access24h',
      titleId: 'ListingPage.highlights.access24h.title',
      descId: 'ListingPage.highlights.access24h.desc',
    });
  }
  if (publicData?.wifi || publicData?.wifi_speed) {
    derivedHighlights.push({
      iconKey: 'wifi',
      titleId: 'ListingPage.highlights.wifi.title',
      descId: 'ListingPage.highlights.wifi.desc',
    });
  }
  if (typeof listingType === 'string' && listingType.toLowerCase().includes('meeting')) {
    derivedHighlights.push({
      iconKey: 'meetingroom',
      titleId: 'ListingPage.highlights.meetingroom.title',
      descId: 'ListingPage.highlights.meetingroom.desc',
    });
  }

  const mosaicImages = currentListing?.images || [];

  // Closed listings, and airport lounges outside Switzerland (decision D3: templated affiliate
  // pages, the Search Console soft-404 pattern), are `noindex, follow`.
  const noIndexMaybe =
    currentListing.attributes.state === LISTING_STATE_CLOSED || !isIndexableListing(currentListing)
      ? { noIndex: true }
      : {};

  return (
    <Page
      title={seoTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={seoDescription}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      {...noIndexMaybe}
      schema={schemaGraph}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        {showListingImage ? (
          <div className={css.heroBanner} data-testid="carousel">
            <SectionPhotoMosaic
              listing={currentListing}
              imageAltTitle={photoAltTitle}
              variantPrefix={config.layout.listingImage.variantPrefix}
              onRequestFullscreen={idx => {
                setGalleryIndex(idx);
                setGalleryOpen(true);
              }}
            />
            {isAbo ? (
              // Mark subscription listings top-right on the hero image (red badge).
              <div className={css.aboDetailBadge}>
                <svg
                  className={css.aboDetailBadgeIcon}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="m17 2 4 4-4 4" />
                  <path d="M3 11v-1a4 4 0 0 1 4-4h14" />
                  <path d="m7 22-4-4 4-4" />
                  <path d="M21 13v1a4 4 0 0 1-4 4H3" />
                </svg>
                <FormattedMessage id="ListingPage.aboBadge" />
              </div>
            ) : null}
          </div>
        ) : null}
        <div className={css.contentWrapperForProductLayout}>
          <div className={css.mainColumnForProductLayout}>
            {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                currentUser={currentUser}
              />
            ) : null}
            {mounted && currentListing.id ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                currentUser={currentUser}
                editParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingPathParamType,
                  tab: listingTab,
                }}
              />
            ) : null}

            {/* Nur auf dem eigenen Inserat und nur, wenn nicht schon die haertere
                Warnung "noch keine Auszahlungsdaten" im Buchungspanel steht —
                zwei Hinweise mit demselben Ziel nebeneinander liest niemand. */}
            {isOwnListing && !noPayoutDetailsSetWithOwnListing ? (
              <VerifiedProviderBanner
                currentUser={currentUser}
                stripeStatusSynced={stripeStatusSynced}
                placement="own_listing"
              />
            ) : null}

            {/* Title + meta (rating · review count · location) */}
            <SectionTitleBar
              title={richTitle}
              location={publicData?.location?.address || publicData?.city}
              locationHref="#listing-location-full"
              rating={avgRating}
              reviewCount={reviews?.length || 0}
              isVerifiedProvider={isVerifiedProviderUser(ensuredAuthor)}
            />

            {/* Booking duration ("how long do you book for") — for every offer. */}
            {bookingDurationUnit ? (
              <span className={css.bookingDurationChip}>
                <svg
                  className={css.bookingDurationIcon}
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
                <FormattedMessage
                  id="BookingDuration.label"
                  values={{ unit: bookingDurationUnit }}
                />
              </span>
            ) : null}

            {/* Award badges from provider profile (Airbnb-style) */}
            <SectionAwardsMaybe
              author={ensuredAuthor}
              rating={avgRating}
              reviewCount={reviews?.length || 0}
            />

            {/* Highlights strip (icon rows under the title, Airbnb-style) */}
            <SectionHighlights highlights={derivedHighlights} />

            {/* Host block — moved up per Airbnb info-column ordering */}
            <SectionAuthorMaybe
              title={title}
              listing={currentListing}
              authorDisplayName={authorDisplayName}
              onContactUser={onContactUser}
              isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
              onCloseInquiryModal={() => setInquiryModalOpen(false)}
              sendInquiryError={sendInquiryError}
              sendInquiryInProgress={sendInquiryInProgress}
              onSubmitInquiry={onSubmitInquiry}
              currentUser={currentUser}
              onManageDisableScrolling={onManageDisableScrolling}
            />

            {/* Description */}
            <SectionDescriptionExpandable
              text={description}
              onManageDisableScrolling={onManageDisableScrolling}
            />

            {/* Features (amenities) — rendered by CustomListingFields'
                multi-enum path, restyled via :global() in ListingPage.module.css */}
            <CustomListingFields
              publicData={publicData}
              metadata={metadata}
              listingFieldConfigs={listingConfig.listingFields}
              categoryConfiguration={config.categoryConfiguration}
              intl={intl}
            />

            {/* What's included — spaces + practicalities (render only when data) */}
            <SectionSpacesMaybe
              publicData={publicData}
              listingFieldConfigs={listingConfig.listingFields}
              intl={intl}
            />
            <SectionPracticalities publicData={publicData} />

            {/* Map + nearby POIs */}
            <SectionLocationFullBleed
              geolocation={geolocation}
              publicData={publicData}
              listingId={currentListing.id}
              mapsConfig={config.maps}
            />

            {/* Reviews */}
            <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />

            {/* Policies */}
            <SectionThingsToKnow
              houseRules={publicData?.rules || publicData?.houseRules}
              cancellationPolicy={publicData?.cancellationPolicy}
              safetyInfo={publicData?.safety || publicData?.safetyInfo}
              onManageDisableScrolling={onManageDisableScrolling}
            />
          </div>
          <div className={css.orderColumnForProductLayout}>
            <OrderPanel
              className={classNames(css.productOrderPanel, {
                [css.imagesEnabled]: showListingImage,
              })}
              listing={currentListing}
              isOwnListing={isOwnListing}
              submitRequiresLogin={!isAuthenticated}
              showAcceptedPaymentMethods
              onSubmit={handleOrderSubmit}
              authorLink={
                <NamedLink
                  className={css.authorNameLink}
                  name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                  params={params}
                  to={{ hash: '#author' }}
                >
                  {authorDisplayName}
                </NamedLink>
              }
              title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
              titleDesktop={
                // h2, not h1: SectionTitleBar carries the page's one visible h1.
                <H4 as="h2" className={css.orderPanelTitleInCard}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              }
              sectionHeadingAs="h2"
              payoutDetailsWarning={payoutDetailsWarning}
              author={ensuredAuthor}
              onManageDisableScrolling={onManageDisableScrolling}
              onContactUser={onContactUser}
              {...restOfProps}
              validListingTypes={config.listing.listingTypes}
              marketplaceCurrency={config.currency}
              dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
              marketplaceName={config.marketplaceName}
              showListingImage={showListingImage}
            />
          </div>
        </div>
        <SectionOtherListingsMaybe listings={authorListings} author={ensuredAuthor} intl={intl} />
        <SectionCityLinkMaybe geolocation={geolocation} />
        <GalleryModal
          images={mosaicImages}
          imageAltTitle={photoAltTitle}
          isOpen={galleryOpen}
          onClose={() => setGalleryOpen(false)}
          initialIndex={galleryIndex}
          onManageDisableScrolling={onManageDisableScrolling}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Array<propTypes.listing>} props.authorListings - Other published listings by the same author
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    authorListingIds,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser, stripeStatusSynced } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    stripeStatusSynced,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    authorListings: getListingsById(state, authorListingIds ?? []),
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onClearInquiryIntent: () => dispatch(clearInquiryIntent()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
